import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ASSISTANT_MESSAGE_MAX_LENGTH,
  ActionProposal,
  AssistantApiError,
  AssistantClientContext,
  AssistantEscalateResponse,
  AssistantLink,
  AssistantMessageAttachment,
  AssistantMessageSender,
  ConfirmActionRequest,
  HumanSupportMessage,
  HumanSupportState,
  ToolCallSummary,
  cancelAssistantAction,
  confirmAssistantAction,
  escalateAssistantConversation,
  friendlyAssistantError,
  getAssistantConversation,
  getHumanSupportUpdates,
  markHumanSupportRead,
  retryHumanSupportMessage,
  sendAssistantFeedback,
  sendAssistantMessage,
  sendHumanSupportMessage,
  setSupportSeenSeq,
} from '../../../services/assistantCentral';
import { getRecentErrors } from '../../../utils/assistantErrorBuffer';
import { computeSupportAssistantComposeDelayMs } from './supportAssistantComposeDelay';

export type AssistantFeedbackState = 'up' | 'down' | null;

export interface AssistantUiMessage {
  localId: string;
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  toolCalls: ToolCallSummary[];
  proposals: ActionProposal[];
  links: AssistantLink[];
  attachments?: AssistantMessageAttachment[];
  isError?: boolean;
  feedback?: AssistantFeedbackState;
  /** Quem escreveu: usuário, IA, atendente da equipe Klinvia ou aviso do sistema. */
  sender: AssistantMessageSender;
  authorName?: string | null;
  hubSeq?: number | null;
  /** Entrega à equipe (só mensagens do usuário no chat humano). */
  delivery?: 'pending' | 'sent' | 'failed' | null;
}

export interface UseAssistantChatOptions {
  /** Chave no sessionStorage para manter a conversa entre navegações. `null` desativa. */
  storageKey?: string | null;
  onConversationChange?: (conversationId: string | null) => void;
  /** Painel visível: liga o polling rápido do chat humano e a confirmação de leitura. */
  active?: boolean;
}

const HUMAN_POLL_MS = 4000;
const HUMAN_POLL_MAX_BACKOFF_MS = 30000;

export const ASSISTANT_WIDGET_STORAGE_KEY = 'klinvia-assistant-conversation-id';

let localCounter = 0;
const nextLocalId = () => {
  localCounter += 1;
  return `m-${Date.now().toString(36)}-${localCounter}`;
};

function fromHumanMessage(m: HumanSupportMessage): AssistantUiMessage {
  return {
    localId: nextLocalId(),
    id: m.id,
    role: m.role,
    sender: m.sender,
    content: m.content || '',
    createdAt: m.created_at,
    toolCalls: [],
    proposals: [],
    links: [],
    attachments: m.attachments?.length ? m.attachments : undefined,
    authorName: m.author_name,
    hubSeq: m.hub_seq,
    delivery: m.delivery,
  };
}

function readStorage(key: string | null | undefined): string | null {
  if (!key) return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string | null | undefined, value: string | null): void {
  if (!key) return;
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // sessionStorage indisponível (modo privado restrito)
  }
}

function buildClientContext(pathname: string): AssistantClientContext {
  const recentErrors = getRecentErrors();
  const appVersion = process.env.REACT_APP_VERSION;
  return {
    pathname,
    pageTitle: typeof document !== 'undefined' ? document.title : undefined,
    ...(recentErrors.length ? { recentErrors } : {}),
    ...(appVersion ? { appVersion } : {}),
  };
}

export function useAssistantChat(options: UseAssistantChatOptions = {}) {
  const { storageKey = null, onConversationChange, active = true } = options;
  const location = useLocation();
  const [conversationId, setConversationId] = useState<string | null>(() => readStorage(storageKey));
  const [messages, setMessages] = useState<AssistantUiMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [canEscalate, setCanEscalate] = useState(false);
  const [escalation, setEscalation] = useState<AssistantEscalateResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [support, setSupport] = useState<HumanSupportState | null>(null);
  const [supportOffline, setSupportOffline] = useState(false);
  const supportRef = useRef(support);
  supportRef.current = support;
  const humanMode = Boolean(support?.human_mode);

  const conversationIdRef = useRef(conversationId);
  const sendingRef = useRef(false);
  const pathnameRef = useRef(location.pathname);
  const loadSeqRef = useRef(0);
  const onConversationChangeRef = useRef(onConversationChange);
  onConversationChangeRef.current = onConversationChange;
  pathnameRef.current = location.pathname;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  type ComposeBatch = {
    localId: string;
    parts: string[];
    images: File[];
    firstAt: number;
    timer: ReturnType<typeof setTimeout>;
  };
  const composeRef = useRef<ComposeBatch | null>(null);

  const clearComposeBatch = useCallback(() => {
    const batch = composeRef.current;
    if (!batch) return;
    clearTimeout(batch.timer);
    composeRef.current = null;
  }, []);

  const applyConversationId = useCallback(
    (id: string | null) => {
      conversationIdRef.current = id;
      setConversationId(id);
      writeStorage(storageKey, id);
      onConversationChangeRef.current?.(id);
    },
    [storageKey],
  );

  const resetState = useCallback(() => {
    loadSeqRef.current += 1;
    setMessages([]);
    setCanEscalate(false);
    setEscalation(null);
    setLoadError(null);
    setLoadingConversation(false);
    setSupport(null);
    setSupportOffline(false);
  }, []);

  const loadConversation = useCallback(
    async (id: string) => {
      const seq = loadSeqRef.current + 1;
      loadSeqRef.current = seq;
      conversationIdRef.current = id;
      setConversationId(id);
      setMessages([]);
      setEscalation(null);
      setLoadError(null);
      setSupport(null);
      setSupportOffline(false);
      setLoadingConversation(true);
      try {
        const detail = await getAssistantConversation(id);
        if (loadSeqRef.current !== seq) return;
        writeStorage(storageKey, id);
        setMessages(
          (detail.messages || []).map((m) => ({
            localId: nextLocalId(),
            id: m.id,
            role: m.role,
            sender: m.sender || (m.role === 'user' ? 'user' : 'ai'),
            authorName: m.author_name ?? null,
            hubSeq: m.hub_seq ?? null,
            delivery: m.delivery ?? null,
            content: m.content || '',
            createdAt: m.created_at,
            toolCalls: m.tool_calls || [],
            proposals: m.proposals || [],
            links: [],
            attachments: m.attachments || [],
          })),
        );
        setSupport(detail.support ?? null);
        const status = String(detail.conversation?.status || '').toLowerCase();
        setCanEscalate(
          !detail.support && status !== 'escalated' && (detail.messages || []).some((m) => m.role === 'assistant'),
        );
      } catch (e) {
        if (loadSeqRef.current !== seq) return;
        if (e instanceof AssistantApiError && (e.status === 404 || e.status === 403)) {
          conversationIdRef.current = null;
          setConversationId(null);
          writeStorage(storageKey, null);
        } else {
          setLoadError(friendlyAssistantError(e, 'Não foi possível carregar a conversa.'));
        }
      } finally {
        if (loadSeqRef.current === seq) setLoadingConversation(false);
      }
    },
    [storageKey],
  );

  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const stored = readStorage(storageKey);
    if (stored) void loadConversation(stored);
  }, [loadConversation, storageKey]);

  const newConversation = useCallback(() => {
    clearComposeBatch();
    resetState();
    applyConversationId(null);
  }, [applyConversationId, resetState, clearComposeBatch]);

  useEffect(() => () => clearComposeBatch(), [clearComposeBatch]);

  const appendAssistantMessage = useCallback((content: string, extra: Partial<AssistantUiMessage> = {}) => {
    setMessages((prev) => [
      ...prev,
      {
        localId: nextLocalId(),
        role: 'assistant',
        sender: 'ai',
        content,
        createdAt: new Date().toISOString(),
        toolCalls: [],
        proposals: [],
        links: [],
        ...extra,
      },
    ]);
  }, []);

  /** Junta mensagens vindas do servidor sem duplicar (pelo id persistido). */
  const mergeServerMessages = useCallback((incoming: HumanSupportMessage[]) => {
    if (!incoming.length) return;
    setMessages((prev) => {
      const known = new Set(prev.map((m) => m.id).filter(Boolean));
      const fresh = incoming.filter((m) => !known.has(m.id)).map(fromHumanMessage);
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, []);

  const sendToHuman = useCallback(
    async (convId: string, message: string, imageFiles: File[] = []) => {
      const images = imageFiles.filter(Boolean);
      const localId = nextLocalId();
      const localAttachments: AssistantMessageAttachment[] = images.map((f) => ({
        mime: f.type || 'image/jpeg',
        preview_url: URL.createObjectURL(f),
        bytes: f.size,
      }));
      setMessages((prev) => [
        ...prev,
        {
          localId,
          role: 'user',
          sender: 'user',
          content: message,
          createdAt: new Date().toISOString(),
          toolCalls: [],
          proposals: [],
          links: [],
          attachments: localAttachments.length ? localAttachments : undefined,
          delivery: 'pending',
        },
      ]);
      try {
        const res = await sendHumanSupportMessage(convId, message, images);
        setMessages((prev) =>
          prev.map((m) =>
            m.localId === localId
              ? {
                  ...m,
                  id: res.message.id,
                  delivery: res.message.delivery || 'sent',
                  attachments: res.message.attachments?.length ? res.message.attachments : m.attachments,
                }
              : m,
          ),
        );
        setSupport(res.support);
        return res.message.delivery !== 'failed';
      } catch {
        setMessages((prev) => prev.map((m) => (m.localId === localId ? { ...m, delivery: 'failed' } : m)));
        return false;
      }
    },
    [],
  );

  const retryHumanMessage = useCallback(async (localId: string) => {
    const convId = conversationIdRef.current;
    const target = messagesRef.current.find((m) => m.localId === localId);
    if (!convId || !target) return;
    if (!target.id) {
      // Nunca chegou ao servidor: reenvia como mensagem nova.
      setMessages((prev) => prev.filter((m) => m.localId !== localId));
      await sendToHuman(convId, target.content);
      return;
    }
    setMessages((prev) => prev.map((m) => (m.localId === localId ? { ...m, delivery: 'pending' } : m)));
    try {
      const res = await retryHumanSupportMessage(convId, target.id);
      setMessages((prev) => prev.map((m) => (m.localId === localId ? { ...m, delivery: res.message.delivery || 'sent' } : m)));
    } catch {
      setMessages((prev) => prev.map((m) => (m.localId === localId ? { ...m, delivery: 'failed' } : m)));
    }
  }, [sendToHuman]);

  /** Polling do chat humano: respostas da equipe e status do chamado. */
  useEffect(() => {
    if (!conversationId || !humanMode || !active) return undefined;
    let alive = true;
    let delay = HUMAN_POLL_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const convId = conversationId;

    const poll = async () => {
      if (!alive) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        timer = setTimeout(poll, HUMAN_POLL_MS);
        return;
      }
      try {
        const res = await getHumanSupportUpdates(convId);
        if (!alive || conversationIdRef.current !== convId) return;
        mergeServerMessages(res.messages);
        if (res.support) setSupport(res.support);
        setSupportOffline(false);
        const seq = res.support?.last_seq || 0;
        if (seq > 0) {
          setSupportSeenSeq(convId, seq);
          if (res.messages.length) void markHumanSupportRead(convId, seq).catch(() => undefined);
        }
        delay = HUMAN_POLL_MS;
      } catch {
        if (!alive) return;
        setSupportOffline(true);
        delay = Math.min(HUMAN_POLL_MAX_BACKOFF_MS, delay * 2);
      }
      if (alive) timer = setTimeout(poll, delay);
    };

    void (async () => {
      await poll();
      // Primeira abertura: confirma leitura do que já estava na conversa.
      const seq = supportRef.current?.last_seq || 0;
      if (alive && seq > 0) void markHumanSupportRead(convId, seq).catch(() => undefined);
    })();

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [conversationId, humanMode, active, mergeServerMessages]);

  const sendToAssistantNow = useCallback(
    async (message: string, images: File[], userLocalId: string) => {
      sendingRef.current = true;
      setSending(true);
      const seq = loadSeqRef.current;
      try {
        const res = await sendAssistantMessage({
          ...(conversationIdRef.current ? { conversation_id: conversationIdRef.current } : {}),
          message,
          ...(images.length ? { images } : {}),
          clientContext: buildClientContext(pathnameRef.current),
        });
        if (loadSeqRef.current !== seq) return true;
        if (res.conversation_id && res.conversation_id !== conversationIdRef.current) {
          applyConversationId(res.conversation_id);
        }
        if (res.mode === 'human') {
          if (res.support) setSupport(res.support);
          if (res.human_message) {
            setMessages((prev) =>
              prev.map((m) =>
                m.localId === userLocalId
                  ? {
                      ...m,
                      id: res.human_message!.id,
                      delivery: res.human_message!.delivery || 'sent',
                      attachments: res.human_message!.attachments?.length
                        ? res.human_message!.attachments
                        : m.attachments,
                    }
                  : m,
              ),
            );
          }
          setCanEscalate(false);
          return true;
        }
        appendAssistantMessage(res.reply || 'Não tenho uma resposta para isso agora.', {
          id: res.message_id,
          toolCalls: res.tool_calls || [],
          proposals: res.proposals || [],
          links: res.links || [],
        });
        if (res.escalation) {
          setEscalation(res.escalation);
          setCanEscalate(false);
          if (res.escalation.message) {
            appendAssistantMessage(res.escalation.message, {
              sender: res.escalation.human_mode ? 'system' : 'ai',
            });
          }
          if (res.escalation.human_mode && res.escalation.hub_synced) {
            setSupport({ protocol: res.escalation.protocol, status: 'open', human_mode: true, last_seq: 0 });
          }
          return true;
        }
        setCanEscalate(Boolean(res.can_escalate));
        return true;
      } catch (e) {
        if (loadSeqRef.current !== seq) return false;
        appendAssistantMessage(
          friendlyAssistantError(
            e,
            'Não consegui responder agora. Tente novamente em instantes ou fale com o suporte.',
          ),
          { isError: true },
        );
        return false;
      } finally {
        sendingRef.current = false;
        setSending(false);
      }
    },
    [appendAssistantMessage, applyConversationId],
  );

  const flushComposeBatch = useCallback(async () => {
    const batch = composeRef.current;
    if (!batch) return false;
    composeRef.current = null;
    clearTimeout(batch.timer);

    const combined = batch.parts.join('\n\n').trim().slice(0, ASSISTANT_MESSAGE_MAX_LENGTH);
    const images = batch.images;
    if (!combined && !images.length) return false;
    if (sendingRef.current) return false;

    const header =
      batch.parts.length > 1
        ? `[O cliente enviou ${batch.parts.length} mensagens em sequência. Considere todas juntas e responda numa única resposta completa.]\n\n`
        : '';
    const payload = `${header}${combined}`.trim();

    setMessages((prev) =>
      prev.map((m) =>
        m.localId === batch.localId
          ? {
              ...m,
              content: combined,
              attachments: images.length
                ? images.map((f) => ({
                    mime: f.type || 'image/jpeg',
                    preview_url: URL.createObjectURL(f),
                    bytes: f.size,
                  }))
                : m.attachments,
            }
          : m,
      ),
    );

    return sendToAssistantNow(payload, images, batch.localId);
  }, [sendToAssistantNow]);

  const scheduleComposeFlush = useCallback(() => {
    const batch = composeRef.current;
    if (!batch) return;
    clearTimeout(batch.timer);
    const hasMedia = batch.images.length > 0;
    const delayMs = computeSupportAssistantComposeDelayMs({ firstAt: batch.firstAt, hasMedia });
    batch.timer = setTimeout(() => {
      void flushComposeBatch();
    }, delayMs);
  }, [flushComposeBatch]);

  const send = useCallback(
    async (text: string, imageFiles: File[] = []) => {
      const part = text.trim();
      const images = imageFiles.filter(Boolean);
      if (!part && !images.length) return false;

      const humanConvId = supportRef.current?.human_mode ? conversationIdRef.current : null;
      if (humanConvId) {
        if (sendingRef.current) return false;
        sendingRef.current = true;
        setSending(true);
        try {
          return await sendToHuman(humanConvId, part.slice(0, ASSISTANT_MESSAGE_MAX_LENGTH), images);
        } finally {
          sendingRef.current = false;
          setSending(false);
        }
      }

      if (sendingRef.current) return false;

      const existing = composeRef.current;
      if (!existing) {
        const localId = nextLocalId();
        const localAttachments: AssistantMessageAttachment[] = images.map((f) => ({
          mime: f.type || 'image/jpeg',
          preview_url: URL.createObjectURL(f),
          bytes: f.size,
        }));
        composeRef.current = {
          localId,
          parts: part ? [part] : [],
          images: [...images],
          firstAt: Date.now(),
          timer: setTimeout(() => undefined, 0),
        };
        setMessages((prev) => [
          ...prev,
          {
            localId,
            role: 'user',
            sender: 'user',
            content: part,
            createdAt: new Date().toISOString(),
            toolCalls: [],
            proposals: [],
            links: [],
            attachments: localAttachments.length ? localAttachments : undefined,
          },
        ]);
      } else {
        if (part) existing.parts.push(part);
        if (images.length) existing.images.push(...images);
        const combined = existing.parts.join('\n\n');
        const allAttachments = existing.images.map((f) => ({
          mime: f.type || 'image/jpeg',
          preview_url: URL.createObjectURL(f),
          bytes: f.size,
        }));
        setMessages((prev) =>
          prev.map((m) =>
            m.localId === existing.localId
              ? {
                  ...m,
                  content: combined,
                  attachments: allAttachments.length ? allAttachments : m.attachments,
                }
              : m,
          ),
        );
      }

      scheduleComposeFlush();
      return true;
    },
    [scheduleComposeFlush, sendToHuman],
  );

  const replaceProposal = useCallback((proposal: ActionProposal) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.proposals.some((p) => p.id === proposal.id)
          ? { ...m, proposals: m.proposals.map((p) => (p.id === proposal.id ? proposal : p)) }
          : m,
      ),
    );
  }, []);

  const patchProposalStatus = useCallback((proposalId: string, status: ActionProposal['status']) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.proposals.some((p) => p.id === proposalId)
          ? { ...m, proposals: m.proposals.map((p) => (p.id === proposalId ? { ...p, status } : p)) }
          : m,
      ),
    );
  }, []);

  const confirmProposal = useCallback(
    async (proposalId: string, body: ConfirmActionRequest) => {
      try {
        const res = await confirmAssistantAction(proposalId, body);
        if (res.proposal) replaceProposal(res.proposal);
        if (res.message) appendAssistantMessage(res.message);
      } catch (e) {
        if (e instanceof AssistantApiError && e.code === 'PROPOSAL_EXPIRED') {
          patchProposalStatus(proposalId, 'expired');
        }
        throw e;
      }
    },
    [appendAssistantMessage, patchProposalStatus, replaceProposal],
  );

  const cancelProposal = useCallback(
    async (proposalId: string) => {
      const res = await cancelAssistantAction(proposalId);
      if (res.proposal) replaceProposal(res.proposal);
      else patchProposalStatus(proposalId, 'cancelled');
    },
    [patchProposalStatus, replaceProposal],
  );

  const sendFeedback = useCallback(async (localId: string, helpful: boolean, comment?: string) => {
    const convId = conversationIdRef.current;
    if (!convId) return;
    const messageId = messagesRef.current.find((m) => m.localId === localId)?.id;
    setMessages((prev) =>
      prev.map((m) => (m.localId === localId ? { ...m, feedback: helpful ? 'up' : 'down' } : m)),
    );
    const trimmed = comment?.trim();
    try {
      await sendAssistantFeedback({
        conversation_id: convId,
        ...(messageId ? { message_id: messageId } : {}),
        helpful,
        ...(trimmed ? { comment: trimmed.slice(0, 1000) } : {}),
      });
    } catch (e) {
      setMessages((prev) => prev.map((m) => (m.localId === localId ? { ...m, feedback: null } : m)));
      throw e;
    }
  }, []);

  const escalate = useCallback(
    async (note?: string) => {
      const convId = conversationIdRef.current;
      if (!convId) throw new AssistantApiError('Envie uma mensagem antes de falar com um humano.');
      const trimmed = note?.trim();
      const res = await escalateAssistantConversation({
        conversation_id: convId,
        ...(trimmed ? { note: trimmed.slice(0, 1000) } : {}),
      });
      setEscalation(res);
      setCanEscalate(false);
      if (res.message) appendAssistantMessage(res.message, { sender: res.human_mode ? 'system' : 'ai' });
      if (res.human_mode && res.hub_synced) {
        setSupport({ protocol: res.protocol, status: 'open', human_mode: true, last_seq: 0 });
      }
      return res;
    },
    [appendAssistantMessage],
  );

  return {
    conversationId,
    messages,
    sending,
    loadingConversation,
    loadError,
    canEscalate,
    escalation,
    send,
    newConversation,
    loadConversation,
    confirmProposal,
    cancelProposal,
    sendFeedback,
    escalate,
    support,
    supportOffline,
    humanMode,
    retryHumanMessage,
  };
}

export type AssistantChatController = ReturnType<typeof useAssistantChat>;
